<template lang="html">
  <div class="date-picker" role="application">
    <slot name="input">
      <date-picker-input
        :class="inputClass"
        :id="id"
        ref="input"
      />
    </slot>
    <span
      :id="descriptionId"
      class="sr-only"
    >{{ (allowFreeKey ? 'Enter a date or ' : '') }}Select a date using arrow keys, page up/down, and shift + page up/down</span>
  </div>
</template>

<script>
import Pikaday from 'pikaday';
import {
  format as formatDate,
  isEqual,
  isValid,
  parse,
  parseJSON,
  addDays,
  addMonths,
  addWeeks,
  addYears
} from 'date-fns';
import { isDate } from 'lodash-es';
import focusable from '../../../mixins/focusable';
import uniqName from '../../../mixins/uniqName';
import DatePickerInput from './DatePickerInput';

const modelEvent = 'input';
const referenceDate = new Date();

export default {
  mixins: [focusable, uniqName],

  components: { DatePickerInput },

  props: {
    allowFreeKey: {
      type: Boolean,
      default: true
    },

    value: {
      type: [String, Date]
    },

    /**
     * Used as the format for displaying values, and also as the default
     * for parsing user input which can be overridden with inputFormats.
     * See https://git.io/fxCyr for accepted date formats.
     */
    format: {
      type: String,
      default: 'M/d/yyyy'
    },

    id: [Number, String],

    /**
     * Optionally provide one or more formats for parsing user
     * input, in addition to "format". Provided formats are
     * attempted in order, with the first valid format chosen.
     * See https://git.io/fxCyr for accepted date formats.
     */
    inputFormats: {
      type: Array,
      default: () => []
    },

    inputClass: String,

    pickerClass: String,

    /**
     * Specify the type that should be emitted with the "input" event.
     * The component will try to guess the correct type to use by matching it to the
     * type of the "value" prop, but the type is indeterminate when the prop is null
     * and defaults to String.
     * NOTE: If allowFreeKey is true and an invalid date is entered, the unparsed string value
     * will be emitted even if the type would normally be a Date.
     * @values Date, String
     */
    emitType: {
      validator: (val) => [Date, String].includes(val)
    },

    /**
     * The range of years to display in the year selection dropdown.
     * See the corresponding configuration option for Pikaday:
     * https://github.com/Pikaday/Pikaday#configuration
     */
    yearRange: [Array, Number]
  },

  data() {
    return {
      isMounted: false
    };
  },

  computed: {
    // [].concat is used instead of spread due to weird IE bug when used in combination with ag-Grid.
    parsingFormats: ({ format, inputFormats }) => [].concat(inputFormats, format),

    // props.value represented as a Date object
    valueAsDate: ({ value: v }) => (isDate(v) ? v : parseJSON(v))
  },

  methods: {
    handleDelete() {
      // If free-key is not allowed, pressing backspace or delete removes the whole value.
      if (!this.allowFreeKey) this.update(null);
    },

    parseInput(val) {
      let date = null;
      const isValidDate = !!val && this.parsingFormats.some((f) => {
        date = parse(val, f, referenceDate);
        return isValid(date);
      });

      if (!isValidDate) {
        // Pikaday doesn't call the onSelect handler (this.update) when anything but a Date object
        // is returned, so call it manually.
        this.update(val || null);

        if (val) {
          // Emit a validation event for a non-empty invalid value.
          this.$emit('invalid', new Error('Invalid Date'));
        }
      }

      return date;
    },

    syncPickerWithValue() {
      this.picker.setDate(this.value ? this.valueAsDate : null);
    },

    update(newVal) {
      // For some reason this is firing before the component is mounted,
      // so wait until it's mounted before handling.
      if (!this.isMounted) return;

      const newValIsDate = isDate(newVal);
      const shouldEmitDate = this.emitType === Date || (!this.emitType && isDate(this.value));
      const newStringVal = newValIsDate ? newVal.toISOString() : newVal;

      // The type of value that's emitted is converted as necessary to match the
      // type of the value prop that was supplied, or the type specified explicitly
      // by the "emitType" prop.
      const emitVal = shouldEmitDate && newValIsDate
        ? newVal
        : newStringVal;

      this.$emit(modelEvent, emitVal);
    },

    shiftDate(modifier) {
      this.picker.setDate(modifier(this.picker.getDate() || new Date()));
    },

    shiftDay(qty) {
      this.shiftDate((d) => addDays(d, qty));
    },

    shiftMonth(qty) {
      this.shiftDate((d) => addMonths(d, qty));
    },

    shiftWeek(qty) {
      this.shiftDate((d) => addWeeks(d, qty));
    },

    shiftYear(qty) {
      this.shiftDate((d) => addYears(d, qty));
    }
  },

  created() {
    this.descriptionId = this.uniqName('description');

    // Workaround for extremely weird IE bug when this component is used in combination
    // with ag-Grid, in which any attempt to access the "name" property of certain
    // objects in the usual way (dot or bracket notation) causes an error in ag-Grid.
    function getName(o) {
      // eslint-disable-next-line no-restricted-syntax
      for (const prop in (o || {})) {
        if (prop === 'name') {
          return o[prop];
        }
      }

      return null;
    }

    // Validate slot content
    const { input } = this.$slots;
    if (input?.length && (
      input.length > 1
      || getName(input[0].componentOptions?.Ctor?.options) !== getName(DatePickerInput)
    )) {
      throw new Error('DatePicker "input" slot only supports a single <date-picker-input> component.');
    }
  },

  watch: {
    value(newVal, oldVal) {
      if (isDate(newVal) && isDate(oldVal) && isEqual(newVal, oldVal)) return;

      this.syncPickerWithValue();
    }
  },

  mounted() {
    const input = this.$children[0].$el;
    this.picker = new Pikaday({
      field: input,
      parse: (val) => this.parseInput(val),
      onSelect: this.update,
      toString: (date) => formatDate(date, this.format),
      theme: this.pickerClass,
      // Pikaday automatically adds an aria-label attribute which interferes with
      // <label> elements, so just remove it.
      onOpen: () => input.removeAttribute('aria-label'),
      yearRange: this.yearRange,
      // Add an arrow next to the year to make it more obvious that it's a dropdown.
      yearSuffix: '<span class="pika-next" style="transform: rotate(90deg);width: 14px;height: 20px;"></span>',
      // Conditionally disable calendar keyboard controls. This is because Pikaday clears the value
      // on backspace/del, and that behavior is not desirable when allowFreeKey is true. Custom
      // arrow key navigation was reimplemented.
      keyboardInput: !this.allowFreeKey
    });

    // Set the initial value for the picker so it doesn't have to rely on parsing
    // the input value, which could be a non-standard format and therefore not
    // reliably parsable.
    this.syncPickerWithValue();

    this.isMounted = true;
  },

  destroyed() {
    this.picker.destroy();
  }
};
</script>

<style lang="scss">
  @import "~pikaday/scss/pikaday";
  @import "../../../styles/common";
</style>

<style lang="css" scoped>
  .date-picker {
    position: relative;
  }
</style>
